/**
 * Small, framework-free formatting helpers shared across the app. Money
 * formatting lives in `@/core/system` (`formatMoney`) — reuse it rather than
 * duplicating a currency formatter here.
 */

/** 'Jan 5' — a short date, no year. Matches the format used across the app. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** 'Jan 5, 2026' — a short date with year, for records spanning years. */
export function formatDateWithYear(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** 'Jan 5, 2026, 3:45 PM' — date and time together. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** '12,345' — grouped digits, no decimals unless the value has them. */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

/**
 * '2:41 PM CDT' today, 'Oct 18, 2:41 PM CDT' another day, with the year when
 * it isn't this one — on the viewer's own clock, zone named. The one way a
 * moment is shown on a live view (docs/LIVE-DATA.md); `timeZone` is for tests.
 */
export function clockText(
  at: string | number,
  now: number = Date.now(),
  timeZone?: string,
): string {
  const date = new Date(at)
  const day = (d: Date) => d.toLocaleDateString('en-US', { timeZone })
  const time = date.toLocaleTimeString('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  if (day(date) === day(new Date(now))) return time
  const sameYear =
    date.toLocaleDateString('en-US', { timeZone, year: 'numeric' }) ===
    new Date(now).toLocaleDateString('en-US', { timeZone, year: 'numeric' })
  const dayText = date.toLocaleDateString('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  return `${dayText}, ${time}`
}

/**
 * When something happened, as a person reads it: 'just now' under a minute,
 * '12 min ago (2:41 PM CDT)' under an hour, then the time itself
 * (`clockText`). Minute-grained on purpose — a view re-renders it at most
 * once a minute, never a ticking seconds counter.
 */
export function whenText(at: string | number, now: number = Date.now(), timeZone?: string): string {
  const ago = now - new Date(at).getTime()
  if (ago < 60_000) return 'just now'
  if (ago < 3_600_000) {
    return `${Math.floor(ago / 60_000)} min ago (${clockText(at, now, timeZone)})`
  }
  return clockText(at, now, timeZone)
}
